import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { Braces, Search, ChevronDown } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL || '';

const FALLBACK_VARS = [
    { value: '{{FirstName}}', label: 'First Name', category: 'contact', type: 'standard' },
    { value: '{{LastName}}', label: 'Last Name', category: 'contact', type: 'standard' },
    { value: '{{Email}}', label: 'Email', category: 'contact', type: 'standard' },
    { value: '{{Title}}', label: 'Job Title', category: 'contact', type: 'standard' },
    { value: '{{CompanyName}}', label: 'Company Name', category: 'account', type: 'standard' },
    { value: '{{Industry}}', label: 'Industry', category: 'account', type: 'standard' },
    { value: '{{SenderName}}', label: 'Your Name', category: 'sender', type: 'standard' },
    { value: '{{SenderEmail}}', label: 'Your Email', category: 'sender', type: 'standard' },
    { value: '{{SenderTitle}}', label: 'Your Position Title', category: 'sender', type: 'standard' },
    { value: '{{SenderPhone}}', label: 'Your Phone', category: 'sender', type: 'standard' },
    { value: '{{SenderLinkedIn}}', label: 'Your LinkedIn', category: 'sender', type: 'standard' },
    { value: '{{SenderTwitter}}', label: 'Your Twitter/X', category: 'sender', type: 'standard' },
    { value: '{{SenderCompany}}', label: 'Your Company', category: 'sender', type: 'standard' },
    { value: '{{SenderWebsite}}', label: 'Your Website', category: 'sender', type: 'standard' },
    { value: '{{SenderBio}}', label: 'Your Bio', category: 'sender', type: 'standard' },
    { value: '{{SenderAddress}}', label: 'Your Address', category: 'sender', type: 'standard' },
    { value: '{{SenderPronouns}}', label: 'Your Pronouns', category: 'sender', type: 'standard' },
];

const CATEGORY_LABELS = {
    contact: 'Contact',
    account: 'Company',
    sender: 'Sender (You)',
    other: 'Other',
};

let cachedVars = null;

export default function MergeVariableDropdown({ onInsert }) {
    const { getAuthHeader } = useAuth();
    const [open, setOpen] = useState(false);
    const [vars, setVars] = useState(cachedVars || FALLBACK_VARS);
    const [search, setSearch] = useState('');
    const [loading, setLoading] = useState(false);
    const ref = useRef(null);

    // Close on outside click
    useEffect(() => {
        const handler = (e) => {
            if (ref.current && !ref.current.contains(e.target)) setOpen(false);
        };
        if (open) document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, [open]);

    // Fetch vars on first open
    useEffect(() => {
        if (!open || cachedVars) return;
        const load = async () => {
            setLoading(true);
            try {
                const res = await axios.get(`${API}/api/admin/custom-fields/merge-variables`, { headers: getAuthHeader() });
                const data = res.data || [];
                if (data.length > 0) {
                    cachedVars = data;
                    setVars(data);
                }
            } catch { /* keep fallback */ }
            finally { setLoading(false); }
        };
        load();
    }, [open, getAuthHeader]);

    const filtered = vars.filter(v =>
        v.label.toLowerCase().includes(search.toLowerCase()) ||
        v.value.toLowerCase().includes(search.toLowerCase())
    );

    // Group by category
    const groups = {};
    filtered.forEach(v => {
        const cat = v.category || 'other';
        if (!groups[cat]) groups[cat] = [];
        groups[cat].push(v);
    });

    const handleSelect = (v) => {
        onInsert(v.value);
        setOpen(false);
        setSearch('');
    };

    return (
        <div className="relative" ref={ref}>
            <button
                onClick={() => setOpen(!open)}
                className={`p-1.5 rounded transition-colors ${open ? 'bg-stone-200 text-stone-900' : 'text-stone-500 hover:bg-stone-100 hover:text-stone-900'}`}
                title="Insert Personalization Variable"
                type="button"
            >
                <Braces className="w-4 h-4" />
            </button>

            {open && (
                <div className="absolute top-full left-0 mt-1 w-64 bg-white rounded-lg border border-stone-200 shadow-lg z-50">
                    {/* Search */}
                    <div className="p-2 border-b border-stone-100">
                        <div className="relative">
                            <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-stone-400" />
                            <input
                                value={search}
                                onChange={e => setSearch(e.target.value)}
                                placeholder="Search variables..."
                                className="w-full pl-7 pr-2 py-1.5 text-xs border border-stone-200 rounded-md focus:outline-none focus:ring-1 focus:ring-stone-400"
                                autoFocus
                            />
                        </div>
                    </div>

                    {/* Variables list */}
                    <div className="max-h-64 overflow-y-auto py-1">
                        {loading && <p className="text-xs text-stone-400 text-center py-3">Loading...</p>}
                        {Object.entries(groups).map(([category, items]) => (
                            <div key={category}>
                                <div className={`px-3 py-1 text-[10px] font-semibold text-stone-400 uppercase tracking-wider ${category === 'sender' ? 'mt-1 border-t border-stone-100 pt-2' : ''}`}>
                                    {CATEGORY_LABELS[category] || category}
                                </div>
                                {items.map(v => (
                                    <button
                                        key={v.value}
                                        onClick={() => handleSelect(v)}
                                        className="w-full flex items-center justify-between px-3 py-1.5 text-xs hover:bg-stone-50 transition-colors text-left"
                                        type="button"
                                    >
                                        <span className="text-stone-700">{v.label}</span>
                                        <code className="text-[10px] text-stone-400 font-mono">{v.value}</code>
                                    </button>
                                ))}
                            </div>
                        ))}
                        {filtered.length === 0 && !loading && (
                            <p className="text-xs text-stone-400 text-center py-3">No variables found</p>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}
